/**
 * Comments service
 * Provides typed functions for interacting with the comments_with_author view
 */

import { supabase } from '../supabase';
import type { CommentWithAuthor } from '../types/database';

/**
 * Fetch all comments for a specific post with author information
 * Uses the comments_with_author view for efficient single-query fetching
 * Orders by created_at ascending (oldest first)
 */
export async function getCommentsByPostId(
  postId: number
): Promise<CommentWithAuthor[]> {
  const { data, error } = await supabase
    .from('comments_with_author')
    .select('*')
    .eq('post_id', postId)
    .order('created_at', { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch comments: ${error.message}`);
  }

  return (data as CommentWithAuthor[]) || [];
}

/**
 * Create a new comment
 * Inserts into the comments table and returns the created comment with author information
 */
export async function createComment(
  postId: number,
  userId: string,
  content: string
): Promise<CommentWithAuthor> {
  // Insert into comments table
  const { data: insertedComment, error: insertError } = await supabase
    .from('comments')
    .insert({
      post_id: postId,
      user_id: userId,
      content: content,
    })
    .select()
    .single();

  if (insertError) {
    throw new Error(`Failed to create comment: ${insertError.message}`);
  }

  // Fetch the created comment from the view to get author information
  const { data: commentWithAuthor, error: fetchError } = await supabase
    .from('comments_with_author')
    .select('*')
    .eq('id', insertedComment.id)
    .single();

  if (fetchError) {
    throw new Error(`Failed to fetch created comment: ${fetchError.message}`);
  }

  return commentWithAuthor as CommentWithAuthor;
}

/**
 * Delete a comment
 * Deletes the comment from the comments table
 */
export async function deleteComment(commentId: number): Promise<void> {
  const { error } = await supabase.from('comments').delete().eq('id', commentId);

  if (error) {
    throw new Error(`Failed to delete comment: ${error.message}`);
  }
}

